<!--
	CouponForm.vue — create a coupon event (coupon_head).

	Spec: docs/specs/2026-09-23-coupon-event-create-design.md §4.

	This file used to be an unrelated WMS/partner purchase-order form that no
	route reached in a useful state and that never called CouponCreate. The
	FILE NAME and the route name (BOCouponForm, routes.js:859 "coupon/:action")
	are kept on purpose so the router, the breadcrumb and every existing link
	stay valid; only the body is the event form.

	TWO GATES, ONE COSMETIC. canCreateCouponEvent decides whether the form is
	rendered at all; it comes from the login body, which the server derives from
	config.yml's coupon_event.emails. It is a UI hint — POST /api/v1/coupon
	re-reads the config on every request (mw.IsCouponEvent), so a stale
	localStorage copy at worst shows a form whose submit answers 403.
-->
<template>
	<div class="content">
		<base-header class="pb-6">
			<div class="row align-items-center py-4">
				<div class="col-lg-6 col-7">
					<h6 class="h2 text-white d-inline-block mb-0">
						{{ $t("coupon_event.title") }}
					</h6>
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</div>

				<div class="col-lg-6 col-5 text-right">
					<!-- spec 2026-10-07-bo-form-draft-clear-design.md §3 F2 / §4 A6:
					     leftmost of the header group; only when the form is shown -->
					<form-clear-button v-if="canCreateCouponEvent" class="mr-2" :disabled="isSaving" @click="clearForm" />
					<router-link to="/backoffice/coupon">
						<base-button size="md" type="neutral">{{
							$t("coupon_event.back")
						}}</base-button>
					</router-link>
				</div>
			</div>
		</base-header>

		<div class="container-fluid mt--6">
			<!--
				Not allowed: the notice INSTEAD of the form, never beside it.
				Rendering a disabled form would invite the operator to fill it
				in and meet a 403 at the end.
			-->
			<card v-if="!canCreateCouponEvent" class="border-0 mb-4" body-classes="px-lg-4 py-lg-4">
				<div class="text-center py-4">
					<i class="fa fa-lock fa-2x text-muted mb-3 d-block"></i>
					<h3 class="mb-2">{{ $t("coupon_event.forbidden_title") }}</h3>
					<p class="text-muted mb-0">{{ $t("coupon_event.forbidden_detail") }}</p>
				</div>
			</card>

			<card v-else class="border-0 mb-4" header-classes="bg-transparent"
				body-classes="px-lg-4 py-lg-4">
				<template slot="header">
					<h3 class="mb-0">{{ $t("coupon_event.title") }}</h3>
					<p class="text-sm text-muted mb-0">{{ $t("coupon_event.subtitle") }}</p>
				</template>

				<el-form ref="eventForm" :model="form" :rules="rules" label-width="220px"
					label-position="top" @submit.native.prevent>
					<div class="row">
						<!-- name -->
						<div class="col-md-6">
							<el-form-item :label="$t('coupon_event.name')" prop="name">
								<el-input v-model="form.name" maxlength="45"
									:placeholder="$t('coupon_event.name_placeholder')"></el-input>
							</el-form-item>
						</div>

						<!-- prefix -->
						<div class="col-md-6">
							<el-form-item :label="$t('coupon_event.prefix')" prop="prefix">
								<el-input v-model="form.prefix" maxlength="10"
									:placeholder="$t('coupon_event.prefix_placeholder')"
									@input="onPrefixInput"></el-input>
								<small class="text-muted">{{ $t("coupon_event.prefix_hint") }}</small>
							</el-form-item>
						</div>

						<!-- url_prefix -->
						<div class="col-md-12">
							<el-form-item :label="$t('coupon_event.url_prefix')" prop="url_prefix">
								<el-input v-model="form.url_prefix"
									:placeholder="$t('coupon_event.url_prefix_placeholder')"></el-input>
								<small class="text-muted">{{ $t("coupon_event.url_prefix_hint") }}</small>
							</el-form-item>
						</div>

						<!--
							valid_from / valid_to.

							value-format is LOAD-BEARING: core.Time on the Go
							side parses exactly "2006-01-02 15:04:05"
							(go-core LocalTime.go UnmarshalJSON). Without it
							el-date-picker binds a Date object, axios serialises
							it as ISO-8601 with a Z, and the bind fails.
						-->
						<div class="col-md-6">
							<el-form-item :label="$t('coupon_event.valid_from')" prop="valid_from">
								<el-date-picker v-model="form.valid_from" type="datetime"
									value-format="yyyy-MM-dd HH:mm:ss" format="yyyy-MM-dd HH:mm:ss"
									:placeholder="$t('coupon_event.valid_from')" class="w-100">
								</el-date-picker>
							</el-form-item>
						</div>

						<div class="col-md-6">
							<el-form-item :label="$t('coupon_event.valid_to')" prop="valid_to">
								<el-date-picker v-model="form.valid_to" type="datetime"
									value-format="yyyy-MM-dd HH:mm:ss" format="yyyy-MM-dd HH:mm:ss"
									:placeholder="$t('coupon_event.valid_to')" class="w-100">
								</el-date-picker>
							</el-form-item>
						</div>

						<!-- running_from / running_to -->
						<div class="col-md-6">
							<el-form-item :label="$t('coupon_event.running_from')" prop="running_from">
								<el-input-number v-model="form.running_from" :min="0" :step="1"
									step-strictly controls-position="right" class="w-100">
								</el-input-number>
							</el-form-item>
						</div>

						<div class="col-md-6">
							<el-form-item :label="$t('coupon_event.running_to')" prop="running_to">
								<el-input-number v-model="form.running_to" :min="0" :step="1"
									step-strictly controls-position="right" class="w-100">
								</el-input-number>
							</el-form-item>
						</div>

						<!--
							quantity — read-only preview of the server's own sum,
							with the cap alongside it. The number goes red the
							moment the range is over, so the operator learns it
							while typing the range rather than on submit.
						-->
						<div class="col-md-6">
							<el-form-item :label="$t('coupon_event.quantity')">
								<div :class="{ 'coupon-event-quantity-over': quantityOverCap }">
									<el-input :value="quantityDisplay" readonly disabled></el-input>
								</div>
								<small class="d-block text-muted">{{
									$t("coupon_event.quantity_hint")
								}}</small>
								<small class="d-block"
									:class="quantityOverCap ? 'text-danger font-weight-bold' : 'text-muted'">
									{{ $t("coupon_event.quantity_max", { max: maxQuantityDisplay }) }}
								</small>
								<small class="d-block text-danger font-weight-bold" v-if="quantityOverCap">
									{{ $t("coupon_event.quantity_over_cap") }}
								</small>
							</el-form-item>
						</div>

						<!-- running_digit -->
						<div class="col-md-6">
							<el-form-item :label="$t('coupon_event.running_digit')" prop="running_digit">
								<el-input-number v-model="form.running_digit" :min="1" :max="10" :step="1"
									step-strictly controls-position="right" class="w-100">
								</el-input-number>
								<small class="text-muted">{{ $t("coupon_event.running_digit_hint") }}</small>
							</el-form-item>
						</div>

						<!-- random_digit -->
						<div class="col-md-6">
							<el-form-item :label="$t('coupon_event.random_digit')" prop="random_digit">
								<el-input-number v-model="form.random_digit" :min="0" :max="10" :step="1" @change="revalidateLinked"
									step-strictly controls-position="right" class="w-100">
								</el-input-number>
								<small class="text-muted">{{ $t("coupon_event.random_digit_hint") }}</small>
							</el-form-item>
						</div>

						<!-- random_round -->
						<div class="col-md-6">
							<el-form-item :label="$t('coupon_event.random_round')" prop="random_round">
								<el-input-number v-model="form.random_round" :min="0" :max="maxRandomRound" :step="1" @change="revalidateLinked"
									step-strictly controls-position="right" class="w-100">
								</el-input-number>
								<small class="text-muted">{{ $t("coupon_event.random_round_hint") }}</small>
							</el-form-item>
						</div>

						<!-- enabled — 0/1; omitted means 1 on the server, so it is sent explicitly -->
						<div class="col-md-12">
							<el-form-item :label="$t('coupon_event.enabled')" prop="enabled">
								<el-switch v-model="form.enabled" :active-value="1" :inactive-value="0"></el-switch>
								<small class="text-muted ml-2">{{ $t("coupon_event.enabled_hint") }}</small>
							</el-form-item>
						</div>

						<!-- condition — optional free text -->
						<div class="col-md-12">
							<el-form-item :label="$t('coupon_event.condition')" prop="condition">
								<el-input v-model="form.condition" type="textarea" :rows="3"
									:placeholder="$t('coupon_event.condition_placeholder')"></el-input>
							</el-form-item>
						</div>
					</div>

					<div class="row mt-3">
						<div class="col-12 text-right">
							<router-link to="/backoffice/coupon">
								<base-button type="secondary" size="md">{{
									$t("coupon_event.cancel")
								}}</base-button>
							</router-link>
							<base-button type="primary" size="md" :disabled="isSaving"
								@click.native="handleSave()">
								<i class="fas fa-spinner fa-spin" v-if="isSaving"></i>
								{{ $t("coupon_event.submit") }}
							</base-button>
						</div>
					</div>
				</el-form>
			</card>
		</div>
	</div>
</template>

<script>
import { Form, FormItem, DatePicker, InputNumber, Input } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import swal from "sweetalert2";
import { mapGetters, mapActions } from "vuex";
import { requestRefresh } from "@/utils/pageRefresh";
import formDraft from "@/mixins/formDraft";
import FormClearButton from "@/components/FormClearButton.vue";
import {
	COUPON_EVENT_MAX_QUANTITY,
	COUPON_EVENT_MAX_RANDOM_ROUND,
	couponEventQuantity,
	couponEventValidate,
	couponEventIsHttpUrl,
	couponEventErrorI18nKey
} from "@/utils/couponEvent";

export default {
	name: "BOCouponForm",
	mixins: [formDraft],
	components: {
		FormClearButton,
		RouteBreadCrumb,
		[Form.name]: Form,
		[FormItem.name]: FormItem,
		[DatePicker.name]: DatePicker,
		[InputNumber.name]: InputNumber,
		[Input.name]: Input
	},
	computed: {
		...mapGetters({
			canCreateCouponEvent: "Auth/canCreateCouponEvent"
		}),

		// quantity — the §4 read-only "จำนวนคูปอง". A PREVIEW: the value that is
		// stored is recomputed by the server, which ignores anything the client
		// sends for it, so this can never disagree with the database.
		//
		// §9.1: range × rounds. The rounds went missing from this sum until
		// round 3, which is how a 999 × 20 event showed "999" on the screen that
		// commissions it and then printed 19,980 codes.
		quantity() {
			return couponEventQuantity(
				this.form.running_from,
				this.form.running_to,
				this.form.random_round
			);
		},
		quantityDisplay() {
			return this.quantity.toLocaleString();
		},

		// The cap is imported, never spelled as a literal in the template or in
		// the copy: it has to change in exactly one place when the BE's
		// CouponEventMaxQuantity changes.
		maxQuantity() {
			return COUPON_EVENT_MAX_QUANTITY;
		},
		maxQuantityDisplay() {
			return COUPON_EVENT_MAX_QUANTITY.toLocaleString();
		},
		quantityOverCap() {
			return this.quantity > COUPON_EVENT_MAX_QUANTITY;
		},
		// §9.1's 0–200 on random_round, imported for the same reason as the cap:
		// the el-input-number's `max` and the validator must be one number.
		maxRandomRound() {
			return COUPON_EVENT_MAX_RANDOM_ROUND;
		},

		// rules — element-ui's per-field mirror of §3. The authority is
		// couponEventValidate (run again in handleSave); these exist so the
		// operator sees WHICH field is wrong while typing, instead of one
		// message at the bottom after pressing save.
		rules() {
			const t = (code) => this.$t("coupon_event.errors." + code);
			return {
				name: [
					{
						required: true,
						message: t("coupon_event_bad_name"),
						trigger: "blur"
					},
					{
						max: 45,
						message: t("coupon_event_bad_name"),
						trigger: "blur"
					}
				],
				prefix: [
					{
						required: true,
						message: t("coupon_event_bad_prefix"),
						trigger: "blur"
					},
					{
						validator: (rule, value, cb) => {
							const v = (value || "").trim().toUpperCase();
							if (v === "" || v.length > 10 || !/^[A-Z0-9]+$/.test(v)) {
								cb(new Error(t("coupon_event_bad_prefix")));
								return;
							}
							cb();
						},
						trigger: "blur"
					}
				],
				url_prefix: [
					{
						required: true,
						message: t("coupon_event_bad_url"),
						trigger: "blur"
					},
					{
						validator: (rule, value, cb) => {
							if (!couponEventIsHttpUrl(value)) {
								cb(new Error(t("coupon_event_bad_url")));
								return;
							}
							cb();
						},
						trigger: "blur"
					}
				],
				valid_from: [
					{
						required: true,
						message: t("coupon_event_bad_dates"),
						trigger: "change"
					}
				],
				valid_to: [
					{
						required: true,
						message: t("coupon_event_bad_dates"),
						trigger: "change"
					},
					{
						validator: (rule, value, cb) => {
							// The pair is only meaningful together, so the
							// ordering rule lives on the second field.
							const res = couponEventValidate({
								...this.form,
								// neutralise every other field so this rule can
								// only ever fire for the dates
								name: "x",
								prefix: "X",
								url_prefix: "https://example.com/",
								running_from: 1,
								running_to: 1,
								running_digit: 1,
								random_digit: 0,
								random_round: 0
							});
							if (!res.ok && res.code === "coupon_event_bad_dates") {
								cb(new Error(t("coupon_event_bad_dates")));
								return;
							}
							cb();
						},
						trigger: "change"
					}
				],
				running_from: [
					{
						required: true,
						message: t("coupon_event_bad_running"),
						trigger: "change"
					}
				],
				running_to: [
					{
						required: true,
						message: t("coupon_event_bad_running"),
						trigger: "change"
					},
					{
						validator: (rule, value, cb) => {
							const from = parseInt(this.form.running_from, 10);
							const to = parseInt(value, 10);
							if (isNaN(from) || isNaN(to) || from < 0 || to < from) {
								cb(new Error(t("coupon_event_bad_running")));
								return;
							}
							// The cap gets its own sentence rather than the
							// generic bad_running text: "เลขรันไม่ถูกต้อง" would
							// not tell the operator that the numbers are fine
							// and it is the print RUN that is too big.
							//
							// §9.1: judged on range × rounds, so this rule has
							// to read random_round too — a range of 10,000 is
							// legal on its own and illegal over 21 rounds.
							if (
								couponEventQuantity(from, to, this.form.random_round) >
								this.maxQuantity
							) {
								cb(
									new Error(
										this.$t("coupon_event.quantity_over_cap")
									)
								);
								return;
							}
							cb();
						},
						trigger: "change"
					}
				],
				running_digit: [
					{
						required: true,
						message: t("coupon_event_bad_digits"),
						trigger: "change"
					},
					{
						validator: (rule, value, cb) => {
							const d = parseInt(value, 10);
							const to = parseInt(this.form.running_to, 10);
							if (isNaN(d) || d < 1 || d > 10) {
								cb(new Error(t("coupon_event_bad_digits")));
								return;
							}
							// Too narrow to spell running_to is not a rounding
							// problem: the generated numbers wrap and two
							// coupons end up with the same code.
							if (!isNaN(to) && d < String(to).length) {
								cb(new Error(t("coupon_event_bad_digits")));
								return;
							}
							cb();
						},
						trigger: "change"
					}
				],
				random_digit: [
					{
						validator: (rule, value, cb) => {
							const v = parseInt(value, 10);
							if (isNaN(v) || v < 0 || v > 10) {
								cb(new Error(t("coupon_event_bad_digits")));
								return;
							}
							cb();
						},
						trigger: "change"
					}
				],
				random_round: [
					{
						validator: (rule, value, cb) => {
							const v = parseInt(value, 10);
							if (isNaN(v) || v < 0 || v > this.maxRandomRound) {
								cb(new Error(t("coupon_event_bad_digits")));
								return;
							}
							// §9.1: rounds need a random segment to differ by.
							// Without one every round reprints the same running
							// numbers, so the server refuses it — and this box
							// is the one the operator has to change.
							if (v > 1 && parseInt(this.form.random_digit, 10) === 0) {
								cb(new Error(t("coupon_event_bad_digits")));
								return;
							}
							cb();
						},
						trigger: "change"
					}
				]
			};
		}
	},
	data() {
		return {
			isSaving: false,
			// Defaults chosen so the form is valid the moment the operator has
			// typed the four things only they can know (name, prefix, url,
			// dates). running_digit 5 covers running_to 99999; the random pair
			// is 0/0, which selects the old single-round PREFIX+RUNNING format.
			form: {
				name: "",
				prefix: "",
				url_prefix: "",
				valid_from: "",
				valid_to: "",
				running_from: 1,
				running_to: 1000,
				running_digit: 5,
				random_digit: 0,
				random_round: 0,
				condition: "",
				enabled: 1
			}
		};
	},
	methods: {
		// formDraft contract (spec 2026-10-07-bo-form-draft-clear-design.md
		// §3 F1, §4 A6): every field back to the data() defaults (name, prefix,
		// url, dates, running range/digits, random pair, condition, enabled)
		// and the el-form's red field messages cleared. isSaving is left to
		// handleSave's finally — afterSaveReset runs mid-save and must not
		// re-open the double-submit guard while the success swal is up.
		resetForm() {
			this.form = this.$options.data.call(this).form;
			this.$nextTick(() => {
				const f = this.$refs.eventForm;
				if (f && typeof f.clearValidate === "function") f.clearValidate();
			});
		},
		// Element UI re-runs a field's rule only when THAT field changes; the
		// running_to and random_round rules read other fields, so a change to
		// random_digit / random_round must re-fire them (verifier r3 #5).
		revalidateLinked() {
			this.$nextTick(() => {
				const f = this.$refs.eventForm;
				if (f && f.validateField) f.validateField(["running_to", "random_round"]);
			});
		},
		...mapActions({
			createData: "Coupon/createData"
		}),

		// The server uppercases the prefix anyway (§3); doing it as the
		// operator types means the code they see on screen is the code that
		// will be printed.
		onPrefixInput(value) {
			this.form.prefix = (value || "").toUpperCase();
		},

		showError(i18nKey) {
			swal.fire({
				title: this.$t(i18nKey),
				text: "",
				icon: "error",
				confirmButtonClass: "btn btn-danger btn-fill",
				buttonsStyling: false
			});
		},

		async handleSave() {
			if (!this.canCreateCouponEvent) {
				this.showError("coupon_event.errors.coupon_event_forbidden");
				return;
			}
			if (this.isSaving) return;

			// The pure §3 sweep is the gate, not el-form's own validate():
			// el-form reports every bad field, this reports the FIRST one in
			// §3 order, which is the same field the server would name.
			const verdict = couponEventValidate(this.form);
			if (!verdict.ok) {
				// An over-cap range comes back as bad_running (the BE reuses the
				// same code), but the generic text would send the operator
				// hunting for a malformed number that is not there. Name the
				// real problem.
				if (verdict.code === "coupon_event_bad_running" && this.quantityOverCap) {
					this.showError("coupon_event.quantity_over_cap");
					if (this.$refs.eventForm) this.$refs.eventForm.validate(() => {});
					return;
				}
				this.showError(couponEventErrorI18nKey(verdict.code));
				if (this.$refs.eventForm) this.$refs.eventForm.validate(() => {});
				return;
			}

			// quantity, bo_user_id and enabled are deliberately NOT sent: §3
			// computes quantity server-side and ignores a client value,
			// bo_user_id comes from the JWT, enabled defaults to 1.
			const payload = {
				name: this.form.name.trim(),
				prefix: this.form.prefix.trim().toUpperCase(),
				url_prefix: this.form.url_prefix.trim(),
				valid_from: this.form.valid_from,
				valid_to: this.form.valid_to,
				running_from: parseInt(this.form.running_from, 10),
				running_to: parseInt(this.form.running_to, 10),
				running_digit: parseInt(this.form.running_digit, 10),
				random_digit: parseInt(this.form.random_digit, 10),
				random_round: parseInt(this.form.random_round, 10),
				condition: this.form.condition || "",
				enabled: this.form.enabled === 0 ? 0 : 1
			};

			this.isSaving = true;
			try {
				const res = await this.createData(payload);
				const head = (res && res.data && res.data.Datum) || {};
				// Saved: empty the cached tab before the swal + push (§4 A6).
				// Errors land in the catch below and keep the draft.
				this.afterSaveReset();

				await swal.fire({
					title: this.$t("coupon_event.created"),
					text: this.$t("coupon_event.created_detail"),
					icon: "success",
					confirmButtonClass: "btn btn-success btn-fill",
					buttonsStyling: false
				});

				// The codes are generated in a background goroutine, so the
				// detail page may be empty for a moment — that is why the swal
				// above says so rather than leaving the operator to wonder.
				if (head.id) {
					requestRefresh("/backoffice/coupon");
					this.$router.push("/backoffice/coupon/detail/" + head.id);
				} else {
					requestRefresh("/backoffice/coupon");
					this.$router.push("/backoffice/coupon");
				}
			} catch (err) {
				const status = (err && err.response && err.response.status) || 0;
				const data = (err && err.response && err.response.data) || {};

				if (status === 403) {
					this.showError("coupon_event.errors.coupon_event_forbidden");
				} else if (status === 400) {
					this.showError(couponEventErrorI18nKey(data.Msg));
				} else {
					this.showError("coupon_event.errors.unknown");
				}
			} finally {
				this.isSaving = false;
			}
		}
	}
};
</script>

<style scoped>
/*
	The quantity field is `disabled`, so element-ui greys its text out and the
	value would stay grey however wrong it is. `>>>` (the deep combinator) is
	used rather than ::v-deep because it works in plain CSS across every
	vue-loader version this repo has been built with.

	#c81e3f, not Argon's $danger #f5365c: tests/unit/contrast.spec.js measures
	every hard-coded `color:` against white and #f5365c is 3.77:1, below WCAG AA.
	This one is 5.66:1 and reads as the same red (mdf-bo-fn#231).
*/
.coupon-event-quantity-over >>> .el-input__inner {
	color: #c81e3f;
	font-weight: 600;
	border-color: #c81e3f;
}
</style>
